import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { pdfApi } from '../services/api';

/**
 * OCR Preview Modal Component
 * Visualizes the PDF to Markdown extraction process with side-by-side view
 */
const OCRPreviewModal = ({ show, onHide, filename }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageData, setPageData] = useState(null);
  const [extractionMethod, setExtractionMethod] = useState('digital'); // 'digital' or 'ocr'
  const [processingStep, setProcessingStep] = useState(0); // 0: not started, 1: loading pdf, 2: extracting, 3: done

  const steps = [
    { label: 'Load PDF', icon: 'fa-file-pdf' },
    { label: 'Extract Text', icon: 'fa-file-alt' },
    { label: 'Convert to Markdown', icon: 'fa-code' },
    { label: 'Complete', icon: 'fa-check' }
  ];

  const loadPagePreview = useCallback(async (page) => {
    if (!filename) return;
    
    setIsLoading(true);
    setError(null);
    setProcessingStep(1);

    try {
      // Simulate the processing steps for visualization
      await new Promise(resolve => setTimeout(resolve, 300));
      setProcessingStep(2);

      const response = await pdfApi.getOCRPreview(filename, page);
      
      setProcessingStep(3);
      await new Promise(resolve => setTimeout(resolve, 200));
      
      setPageData(response);
      setTotalPages(response.total_pages || 1);
      setExtractionMethod(response.extraction_method || 'digital');
      setProcessingStep(4);
    } catch (err) {
      console.error('OCR Preview error:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load OCR preview');
      setProcessingStep(0);
    } finally {
      setIsLoading(false);
    }
  }, [filename]);

  useEffect(() => {
    if (show && filename) {
      setCurrentPage(1);
      setProcessingStep(0);
      loadPagePreview(1);
    }
  }, [show, filename, loadPagePreview]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      loadPagePreview(newPage);
    }
  };

  const getStepStatus = (stepIndex) => {
    if (processingStep > stepIndex + 1) return 'completed';
    if (processingStep === stepIndex + 1) return 'active';
    return '';
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered className="ocr-modal">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="fas fa-magic text-ut-orange"></i>
          PDF to Markdown Preview
          {filename && (
            <Badge bg="secondary" className="ms-2 fw-normal">
              {filename}
            </Badge>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Processing Steps Indicator */}
        <div className="ocr-step-indicator">
          {steps.map((step, index) => (
            <div key={index} className={`ocr-step ${getStepStatus(index)}`}>
              <span className="ocr-step-number">
                {getStepStatus(index) === 'completed' ? (
                  <i className="fas fa-check"></i>
                ) : (
                  index + 1
                )}
              </span>
              <i className={`fas ${step.icon}`}></i>
              <span>{step.label}</span>
            </div>
          ))}
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}

        {isLoading && processingStep < 4 ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p className="text-muted mb-0">
              {processingStep === 1 && 'Loading PDF document...'}
              {processingStep === 2 && 'Extracting text content...'}
              {processingStep === 3 && 'Converting to Markdown...'}
            </p>
          </div>
        ) : pageData ? (
          <>
            {/* Page Navigation */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center mb-3">
                <div className="ocr-page-nav">
                  <button 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1 || isLoading}
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <span className="ocr-page-info">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages || isLoading}
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            )}

            {/* Side-by-side Preview */}
            <div className="ocr-preview-container">
              {/* PDF View */}
              <div className="ocr-preview-pane">
                <div className="ocr-preview-header">
                  <h6>
                    <i className="fas fa-file-pdf text-danger"></i>
                    Original PDF
                  </h6>
                  <Badge className={extractionMethod === 'ocr' ? 'badge-ocr' : 'badge-extraction'}>
                    {extractionMethod === 'ocr' ? 'OCR Required' : 'Digital Text'}
                  </Badge>
                </div>
                <div className="ocr-preview-content ocr-pdf-view">
                  {pageData.page_image ? (
                    <img 
                      src={`data:image/png;base64,${pageData.page_image}`}
                      alt={`Page ${currentPage}`}
                    />
                  ) : (
                    <div className="text-center text-muted p-4">
                      <i className="fas fa-file-pdf fa-3x mb-3"></i>
                      <p>PDF preview not available</p>
                      <small>View the PDF directly to see the content</small>
                    </div>
                  )}
                </div>
              </div>

              {/* Markdown View */}
              <div className="ocr-preview-pane">
                <div className="ocr-preview-header">
                  <h6>
                    <i className="fas fa-code text-ut-blue"></i>
                    Extracted Markdown
                  </h6>
                  {pageData.char_count && (
                    <small className="text-muted">
                      {pageData.char_count.toLocaleString()} chars
                    </small>
                  )}
                </div>
                <div className="ocr-preview-content">
                  <pre className="ocr-markdown-view">
                    {pageData.markdown_content || 'No text content extracted'}
                  </pre>
                </div>
              </div>
            </div>

            {/* Extraction Info */}
            {pageData.extraction_info && (
              <div className="mt-3 p-3 bg-ut-light rounded">
                <small className="text-muted">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Extraction Details:</strong> {' '}
                  Method: {pageData.extraction_info.method || extractionMethod}, 
                  Text Length: {pageData.extraction_info.text_length?.toLocaleString() || 0}, 
                  Cleaned: {pageData.extraction_info.cleaned_length?.toLocaleString() || 0} chars
                </small>
              </div>
            )}
          </>
        ) : !error && (
          <div className="text-center py-5 text-muted">
            <i className="fas fa-file-alt fa-3x mb-3"></i>
            <p>Select a PDF to preview the extraction process</p>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <small className="text-muted">
            {extractionMethod === 'ocr' ? (
              <><i className="fas fa-eye me-1"></i> Using OCR for scanned documents</>
            ) : (
              <><i className="fas fa-file-alt me-1"></i> Using digital text extraction</>
            )}
          </small>
          <div>
            <Button variant="ut-secondary" onClick={onHide}>
              Close
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default OCRPreviewModal;
